import React, { useEffect, useMemo, useState } from 'react';

import { useNavigate } from 'react-router-dom';

import { useQuery } from '@tanstack/react-query';

import toast from 'react-hot-toast';

import { Mail, Phone, Search } from 'lucide-react';

import Modal from '../ui/Modal';

import { useDebounce } from '../../hooks/useDebounce';

import { Button } from '../ui/button';

import { Label } from '../ui/label';

import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';

import { apiService } from '../../services/api';

import { formatPhoneNumber } from '../../utils/formatters';

import {

  Category,

  Client,

  ClientLocationDto,

  WorkOrderEmailImportPreview,

  WorkOrderType,

  WorkOrderWizardData,

} from '../../types';



interface EmailImportModalProps {

  isOpen: boolean;

  onClose: () => void;

}



const EmailImportModal: React.FC<EmailImportModalProps> = ({ isOpen, onClose }) => {

  const navigate = useNavigate();

  const [emailText, setEmailText] = useState('');

  const [preview, setPreview] = useState<WorkOrderEmailImportPreview | null>(null);

  const [description, setDescription] = useState('');

  const [selectedClient, setSelectedClient] = useState<Client | undefined>();

  const [selectedLocationId, setSelectedLocationId] = useState<number | undefined>();

  const [selectedCategoryId, setSelectedCategoryId] = useState<number | undefined>();

  const [isParsing, setIsParsing] = useState(false);

  const [isContinuing, setIsContinuing] = useState(false);



  const [clientSearchTerm, setClientSearchTerm] = useState('');

  const debouncedClientSearchTerm = useDebounce(clientSearchTerm, 500);

  const [clientPage, setClientPage] = useState(1);

  const clientPageSize = 10;

  const clientSortField = 'name';

  const clientSortDirection: 'asc' | 'desc' = 'asc';



  const [locationSearchTerm, setLocationSearchTerm] = useState('');

  const debouncedLocationSearchTerm = useDebounce(locationSearchTerm, 300);



  const { data: categoriesResponse } = useQuery({

    queryKey: ['categories-import'],

    queryFn: () => apiService.getCategories(1, 200),

    enabled: isOpen,

    staleTime: 60_000,

  });



  const { data: clientsResponse, isLoading: clientsLoading } = useQuery({

    queryKey: ['clients-import', clientPage, clientPageSize, debouncedClientSearchTerm, clientSortField, clientSortDirection],

    queryFn: () => apiService.getClients(

      clientPage,

      clientPageSize,

      debouncedClientSearchTerm || undefined,

      clientSortField,

      clientSortDirection

    ),

    enabled: isOpen && !!preview,

    staleTime: 60_000,

  });



  const { data: locationsResponse, isLoading: locationsLoading } = useQuery({

    queryKey: ['clientLocations-import', selectedClient?.id],

    queryFn: async () => {

      const res = await apiService.getClientLocations(selectedClient!.id);

      return (res.data || []) as ClientLocationDto[];

    },

    enabled: isOpen && !!selectedClient?.id,

    staleTime: 30_000,

  });



  const clients: Client[] = clientsResponse?.data?.data || [];

  const clientTotalPages = clientsResponse?.data?.totalPages || 1;

  const locations: ClientLocationDto[] = locationsResponse || [];

  const allCategories: Category[] = categoriesResponse?.data?.data || [];



  const suggestedLocationIds = useMemo(
    () => new Set((preview?.locationSuggestions || []).map(item => item.clientLocationId)),
    [preview?.locationSuggestions]
  );



  const getClientSuggestion = (clientId: number) =>

    preview?.clientSuggestions?.find(item => item.clientId === clientId);



  const filteredLocations = useMemo(() => {

    if (!debouncedLocationSearchTerm.trim()) {

      return locations;

    }



    const searchLower = debouncedLocationSearchTerm.toLowerCase();

    return locations.filter((loc) => {

      const locationName = loc.locationName?.toLowerCase() || '';

      const city = loc.city?.toLowerCase() || '';

      const state = loc.state?.toLowerCase() || '';

      const zipCode = loc.zipCode?.toLowerCase() || '';

      const address = loc.address?.toLowerCase() || '';

      const contactName = loc.contactName?.toLowerCase() || '';

      const contactPhone = loc.contactPhone?.toLowerCase() || '';

      const contactEmail = loc.contactEmail?.toLowerCase() || '';



      return (

        locationName.includes(searchLower)

        || city.includes(searchLower)

        || state.includes(searchLower)

        || zipCode.includes(searchLower)

        || address.includes(searchLower)

        || contactName.includes(searchLower)

        || contactPhone.includes(searchLower)

        || contactEmail.includes(searchLower)

      );

    });

  }, [locations, debouncedLocationSearchTerm]);



  const categoryOptions = useMemo(() => {

    const suggestedIds = new Set((preview?.categorySuggestions || []).map(item => item.categoryId));

    const suggested = preview?.categorySuggestions || [];

    const additional = allCategories

      .filter(category => !suggestedIds.has(category.id))

      .map(category => ({

        categoryId: category.id,

        categoryName: category.name,

        matchScore: 0,

        matchedOn: 'Manual',

      }));



    return [...suggested, ...additional];

  }, [allCategories, preview?.categorySuggestions]);



  const handleClientSearch = (term: string) => {

    setClientSearchTerm(term);

    setClientPage(1);

  };



  const handleClientSelect = (client: Client) => {

    setSelectedClient(client);

    setSelectedLocationId(client.primaryLocationId);

    setLocationSearchTerm('');

  };



  const handleLocationSelect = (location: ClientLocationDto) => {

    setSelectedLocationId(location.id);

  };



  const applySuggestedClient = async (clientId?: number, locationId?: number) => {

    if (!clientId) {

      return;

    }



    try {

      const clientResponse = await apiService.getClient(clientId);

      if (!clientResponse.success || !clientResponse.data) {

        return;

      }



      setSelectedClient(clientResponse.data);

      setSelectedLocationId(locationId ?? clientResponse.data.primaryLocationId);

    } catch (error) {

      console.error('Failed to load suggested client:', error);

    }

  };



  useEffect(() => {

    if (!isOpen) {

      setEmailText('');

      setPreview(null);

      setDescription('');

      setSelectedClient(undefined);

      setSelectedLocationId(undefined);

      setSelectedCategoryId(undefined);

      setClientSearchTerm('');

      setClientPage(1);

      setLocationSearchTerm('');

      setIsParsing(false);

      setIsContinuing(false);

    }

  }, [isOpen]);



  const handleParse = async () => {

    if (!emailText.trim()) {

      toast.error('Paste the email content before parsing.');

      return;

    }



    setIsParsing(true);

    try {

      const response = await apiService.previewWorkOrderEmailImport(emailText);

      if (!response.success || !response.data) {

        toast.error(response.message || 'Failed to parse email.');

        return;

      }



      const data = response.data;

      setPreview(data);

      setDescription(data.extractedRaw.description || data.suggestedCreateWorkOrder.description || '');

      setSelectedCategoryId(data.suggestedCreateWorkOrder.categoryId);

      setClientSearchTerm(data.extractedRaw.customerName || '');

      setClientPage(1);

      setLocationSearchTerm('');



      const extractedAddress = [

        data.extractedRaw.streetAddress,

        data.extractedRaw.city,

        data.extractedRaw.state,

        data.extractedRaw.zipCode,

      ].filter(Boolean).join(' ');



      if (extractedAddress) {

        setLocationSearchTerm(extractedAddress);

      }



      await applySuggestedClient(

        data.suggestedCreateWorkOrder.clientId,

        data.suggestedCreateWorkOrder.clientLocationId

      );

    } catch (error) {

      console.error('Email import parse failed:', error);

      toast.error('Failed to parse email.');

    } finally {

      setIsParsing(false);

    }

  };



  const canContinue = !!preview

    && !!selectedClient

    && !!selectedCategoryId

    && (locations.length === 0 || !!selectedLocationId);



  const handleContinue = async () => {

    if (!preview || !selectedClient || !selectedCategoryId) {

      toast.error('Select a client and service category before continuing.');

      return;

    }



    if (locations.length > 0 && !selectedLocationId) {

      toast.error('Select a client location before continuing.');

      return;

    }



    setIsContinuing(true);

    try {

      const selectedLocation = locations.find(location => location.id === selectedLocationId);

      const suggested = preview.suggestedCreateWorkOrder;

      const externalWorkOrderNumber =

        preview.extractedRaw.externalWorkOrderNumber

        || suggested.externalWorkOrderNumber

        || undefined;

      const prefilledWizardData: WorkOrderWizardData = {

        selectedVendors: [],

        fromEmailImport: true,

        selectedClient,

        selectedClientLocationId: selectedLocationId,

        selectedClientLocationName: selectedLocation?.locationName,

        selectedClientLocationCity: selectedLocation?.city,

        selectedClientLocationState: selectedLocation?.state,

        selectedClientLocationZipCode: selectedLocation?.zipCode,

        zipcode: selectedLocation?.zipCode || preview.extractedRaw.zipCode || '',

        radius: 50,

        selectedCategories: [selectedCategoryId],

        assignedUserId: undefined,

        priorityId: suggested.priorityId,

        statusId: suggested.statusId,

        categoryId: selectedCategoryId,

        type: suggested.type ?? WorkOrderType.Regular,

        dueDate: suggested.dueDate,

        externalWorkOrderNumber,

        nteAmount: suggested.nteAmount,

        description,

      };



      onClose();

      navigate('/workorders/create', { state: { prefilledWizardData }, replace: true });

    } catch (error) {

      console.error('Email import continue failed:', error);

      toast.error('Failed to continue to work order creation.');

    } finally {

      setIsContinuing(false);

    }

  };



  return (

    <Modal

      isOpen={isOpen}

      onClose={onClose}

      title="Import Work Order from Email"

      maxWidth="3xl"

      closeOnBackdropClick={false}

    >

      <div className="space-y-6">

        <div>

          <Label htmlFor="email-import-text">Paste email content</Label>

          <textarea

            id="email-import-text"

            value={emailText}

            onChange={(event) => setEmailText(event.target.value)}

            rows={10}

            className="mt-2 w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 px-3 py-2 text-sm text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-accent-color"

            placeholder="Paste a work order email here..."

            aria-label="Paste email content"

          />

        </div>



        <div className="flex justify-end">

          <Button type="button" onClick={handleParse} disabled={isParsing || !emailText.trim()}>

            {isParsing ? 'Parsing...' : 'Parse Email'}

          </Button>

        </div>



        {preview && (

          <div className="space-y-4 border-t border-gray-200 dark:border-gray-700 pt-4">

            <div className="flex flex-wrap items-center gap-3 text-sm text-gray-600 dark:text-gray-300">

              <span>Parser: <strong>{preview.parserUsed}</strong></span>

              <span>Confidence: <strong>{preview.overallConfidence}</strong></span>

            </div>



            {preview.warnings.length > 0 && (

              <div className="rounded-md border border-amber-300 bg-amber-50 dark:bg-amber-900/20 dark:border-amber-700 px-4 py-3 text-sm text-amber-900 dark:text-amber-100">

                <ul className="list-disc pl-5 space-y-1">

                  {preview.warnings.map((warning) => (

                    <li key={warning}>{warning}</li>

                  ))}

                </ul>

              </div>

            )}



            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">

              <div>

                <Label>External Work Order #</Label>

                <div className="mt-1 text-sm text-gray-900 dark:text-gray-100">

                  {preview.extractedRaw.externalWorkOrderNumber

                    || preview.suggestedCreateWorkOrder.externalWorkOrderNumber

                    || '—'}

                </div>

              </div>

              <div>

                <Label>NTE Amount (DO NOT EXCEED)</Label>

                <div className="mt-1 text-sm text-gray-900 dark:text-gray-100">

                  {preview.suggestedCreateWorkOrder.nteAmount != null

                    ? `$${preview.suggestedCreateWorkOrder.nteAmount.toFixed(2)}`

                    : '—'}

                </div>

              </div>

              <div>

                <Label>Status</Label>

                <div className="mt-1 text-sm text-gray-900 dark:text-gray-100">

                  {preview.suggestedCreateWorkOrder.statusName || 'Open (Unassigned)'}

                </div>

              </div>

              <div>

                <Label>Priority</Label>

                <div className="mt-1 text-sm text-gray-900 dark:text-gray-100">

                  {preview.suggestedCreateWorkOrder.priorityName || 'Medium'}

                </div>

              </div>

              <div>

                <Label>Due Date</Label>

                <div className="mt-1 text-sm text-gray-900 dark:text-gray-100">

                  {preview.suggestedCreateWorkOrder.dueDate

                    ? new Date(preview.suggestedCreateWorkOrder.dueDate).toLocaleString()

                    : '—'}

                </div>

              </div>

              <div>

                <Label>Email Priority (reference only)</Label>

                <div className="mt-1 text-sm text-gray-900 dark:text-gray-100">

                  {preview.extractedRaw.emailPriority || '—'}

                </div>

              </div>

            </div>



            <div>

              <Label htmlFor="email-import-description">Description (Problem + Note)</Label>

              <textarea

                id="email-import-description"

                value={description}

                onChange={(event) => setDescription(event.target.value)}

                rows={6}

                className="mt-2 w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 px-3 py-2 text-sm text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-accent-color"

                aria-label="Imported description"

              />

            </div>



            <div>

              <Label htmlFor="email-import-client-search">Select Client *</Label>

              {preview.extractedRaw.customerName && (

                <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">

                  From email: {preview.extractedRaw.customerName}

                </p>

              )}

              <div className="relative mt-2 mb-3">

                <Search className="h-4 w-4 absolute left-3 top-3 text-gray-400" />

                <input

                  id="email-import-client-search"

                  type="text"

                  value={clientSearchTerm}

                  onChange={(event) => handleClientSearch(event.target.value)}

                  placeholder="Search by name, address, phone, email, or location..."

                  className="w-full pl-9 pr-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-accent-color"

                  aria-label="Search clients"

                />

              </div>

              <div className="max-h-64 overflow-y-auto border border-gray-200 dark:border-gray-700 rounded-md divide-y divide-gray-100 dark:divide-gray-700 bg-white dark:bg-gray-800">

                {clientsLoading ? (

                  <div className="p-3 text-sm text-gray-500">Loading clients…</div>

                ) : clients.length === 0 ? (

                  <div className="p-3 text-sm text-gray-500">No clients found</div>

                ) : (

                  clients.map((client) => {

                    const suggestion = getClientSuggestion(client.id);

                    return (

                      <label

                        key={client.id}

                        className="flex items-center justify-between p-3 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700"

                      >

                        <div>

                          <div className="font-medium text-gray-900 dark:text-gray-100">

                            {client.name}

                            {suggestion && (

                              <span className="ml-2 text-xs font-normal text-accent-color">

                                ({suggestion.matchScore}% match)

                              </span>

                            )}

                          </div>

                          {client.primaryLocationName && (

                            <>

                              <div className="text-xs text-gray-600 dark:text-gray-300">

                                {client.primaryLocationName.replace(' - Primary Location', '')}

                              </div>

                              <div className="text-xs text-gray-500 dark:text-gray-400">

                                {[client.primaryLocationCity, client.primaryLocationState, client.primaryLocationZipCode].filter(Boolean).join(', ')}

                              </div>

                            </>

                          )}

                          <div className="mt-1 flex items-center gap-3 text-xs text-gray-500 dark:text-gray-400">

                            {client.phone && (

                              <span className="inline-flex items-center gap-1">

                                <Phone className="h-3 w-3" /> {formatPhoneNumber(client.phone)}

                              </span>

                            )}

                            {client.email && (

                              <span className="inline-flex items-center gap-1">

                                <Mail className="h-3 w-3" /> {client.email}

                              </span>

                            )}

                          </div>

                        </div>

                        <input

                          type="radio"

                          name="emailImportClientSelection"

                          checked={selectedClient?.id === client.id}

                          onChange={() => handleClientSelect(client)}

                          className="h-4 w-4 text-accent-color focus:ring-accent-color border-gray-300 dark:border-gray-600"

                        />

                      </label>

                    );

                  })

                )}

              </div>

              {clientTotalPages > 1 && (

                <div className="flex justify-end gap-2 mt-2">

                  <button

                    type="button"

                    onClick={() => setClientPage(Math.max(1, clientPage - 1))}

                    disabled={clientPage === 1}

                    className="px-3 py-1 text-sm border rounded disabled:opacity-50"

                  >

                    Prev

                  </button>

                  <div className="text-sm text-gray-600 dark:text-gray-400 px-1">

                    Page {clientPage} of {clientTotalPages}

                  </div>

                  <button

                    type="button"

                    onClick={() => setClientPage(Math.min(clientTotalPages, clientPage + 1))}

                    disabled={clientPage === clientTotalPages}

                    className="px-3 py-1 text-sm border rounded disabled:opacity-50"

                  >

                    Next

                  </button>

                </div>

              )}

            </div>



            {selectedClient && (

              <div>

                <Label htmlFor="email-import-location-search">Select Location *</Label>

                <div className="relative mt-2 mb-3">

                  <Search className="h-4 w-4 absolute left-3 top-3 text-gray-400" />

                  <input

                    id="email-import-location-search"

                    type="text"

                    value={locationSearchTerm}

                    onChange={(event) => setLocationSearchTerm(event.target.value)}

                    placeholder="Search locations by name, address, city, state, zip, or contact..."

                    className="w-full pl-9 pr-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-accent-color"

                    aria-label="Search locations"

                  />

                </div>

                <div className="max-h-64 overflow-y-auto border border-gray-200 dark:border-gray-700 rounded-md divide-y divide-gray-100 dark:divide-gray-700 bg-white dark:bg-gray-800">

                  {locationsLoading ? (

                    <div className="p-3 text-sm text-gray-500">Loading locations…</div>

                  ) : filteredLocations.length === 0 ? (

                    <div className="p-3 text-sm text-gray-500">

                      {locations.length === 0

                        ? 'No locations found for this client'

                        : `No locations match "${locationSearchTerm}"`}

                    </div>

                  ) : (

                    filteredLocations.map((location) => {

                      const suggestion = preview.locationSuggestions?.find(

                        item => item.clientLocationId === location.id

                      );

                      const isSuggested = suggestedLocationIds.has(location.id);



                      return (

                        <label

                          key={location.id}

                          className="flex items-center justify-between p-3 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700"

                        >

                          <div>

                            <div className="font-medium text-gray-900 dark:text-gray-100">

                              {location.locationName?.replace(' - Primary Location', '')}

                              {isSuggested && suggestion && (

                                <span className="ml-2 text-xs font-normal text-accent-color">

                                  ({suggestion.matchScore}% match)

                                </span>

                              )}

                            </div>

                            <div className="text-xs text-gray-500 dark:text-gray-400">

                              {[location.address, location.city, location.state, location.zipCode].filter(Boolean).join(', ')}

                            </div>

                            <div className="mt-1 flex items-center gap-3 text-xs text-gray-500 dark:text-gray-400">

                              {location.contactPhone && (

                                <span className="inline-flex items-center gap-1">

                                  <Phone className="h-3 w-3" /> {formatPhoneNumber(location.contactPhone)}

                                </span>

                              )}

                              {location.contactEmail && (

                                <span className="inline-flex items-center gap-1">

                                  <Mail className="h-3 w-3" /> {location.contactEmail}

                                </span>

                              )}

                            </div>

                          </div>

                          <input

                            type="radio"

                            name="emailImportLocationSelection"

                            checked={selectedLocationId === location.id}

                            onChange={() => handleLocationSelect(location)}

                            className="h-4 w-4 text-accent-color focus:ring-accent-color border-gray-300 dark:border-gray-600"

                          />

                        </label>

                      );

                    })

                  )}

                </div>

              </div>

            )}



            <div>

              <Label>Service Category *</Label>

              <Select

                value={selectedCategoryId ? String(selectedCategoryId) : undefined}

                onValueChange={(value) => setSelectedCategoryId(Number(value))}

              >

                <SelectTrigger className="mt-2">

                  <SelectValue placeholder="Select service category" />

                </SelectTrigger>

                <SelectContent className="z-[60]">

                  {categoryOptions.map((category) => (

                    <SelectItem key={category.categoryId} value={String(category.categoryId)}>

                      {category.categoryName}

                      {category.matchScore > 0 ? ` (${category.matchScore}% match)` : ''}

                    </SelectItem>

                  ))}

                </SelectContent>

              </Select>

            </div>



            <div className="flex justify-end gap-3 pt-2">

              <Button type="button" variant="outline" onClick={onClose}>

                Cancel

              </Button>

              <Button type="button" onClick={handleContinue} disabled={!canContinue || isContinuing}>

                {isContinuing ? 'Loading...' : 'Continue to Create Work Order'}

              </Button>

            </div>

          </div>

        )}

      </div>

    </Modal>

  );

};



export default EmailImportModal;

